import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Button,
  Stack,
  IconButton,
  Input,
  useToast,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon } from "@chakra-ui/icons";
import EditBookModal_Trs from "./EditBookModal_Trs";
import axios from "axios";
import { BASE_URL } from "../../../../../Globalapi/Globalapi";

const AssignedTeachersBook = () => {
  const [data, setData] = useState([]);
  const [books, setBooks] = useState([]); // Store list of books
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(""); // For teacher name search
  const [selectedBook, setSelectedBook] = useState(""); // For book filter
  const toast = useToast();

  const schoolId = sessionStorage.getItem("school_id"); // Get school_id from sessionStorage

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${BASE_URL}/admin/library/issues/list_issue.php?school_id=${schoolId}`
        );
        const result = await response.json();
        if (result.success) {
          // Ensure we are only including rows with member_type as "Teacher"
          const filteredData = result.data.filter(
            (row) => row.member_type === "Teacher"
          );
          setData(filteredData);
        } else {
          console.error("Failed to fetch data:", result.message);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchBooks = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/admin/library/books/list_books.php?school_id=${schoolId}`
        );
        if (response.data.success) {
          setBooks(
            Array.isArray(response.data.data)
              ? response.data.data
              : [response.data.data]
          );
        }
      } catch (error) {
        console.error("Error fetching books:", error);
      }
    };

    if (schoolId) {
      fetchData();
      fetchBooks();
    }
  }, [schoolId]);

  const deleteIssue = async (issueId) => {
    try {
      const response = await fetch(
        `${BASE_URL}/admin/library/issues/delete_issue.php`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            issue_id: issueId,
            school_id: schoolId,
            user_id: sessionStorage.getItem("user_id"),
          }),
        }
      );
      const result = await response.json();

      if (result.success) {
        setData((prevData) => prevData.filter((item) => item.id !== issueId));
        toast({
          title: "Success",
          description: "Book issue deleted successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: result.message || "Failed to delete book issue.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error deleting issue:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const filteredData = data.filter((row) => {
    const matchesSearch = row.member_name
      ?.toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesBookFilter = selectedBook ? row.title === selectedBook : true;
    return matchesSearch && matchesBookFilter;
  });

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const currentData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when rows per page changes
  };

  return (
    <Box>
      <Flex justify="space-between" className="sb" p={3} >
        <Select
          width="120px"
          value={rowsPerPage}
          onChange={handleRowsPerPageChange}
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
        </Select>
        <Flex direction="column" width="100%">
          <Flex
            gap={4}
            direction={{ base: "column", md: "row" }} // Vertical on small screens, horizontal on large screens
            width="100%"
            justify={{ base: "flex-start", md: "flex-end" }} // Left-aligned on small screens, right-aligned on larger screens
          >
            <Input
              ml={1}
              placeholder="Search Teacher Name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              width={{ base: "100%", md: "250px" }} // Full width on small screens, fixed width on large screens
              // mr={4}
            />
            <Select
              placeholder="Filter by Book"
              value={selectedBook}
              onChange={(e) => setSelectedBook(e.target.value)}
              width={{ base: "100%", md: "250px" }} // Full width on small screens, fixed width on large screens
              // mr={4}
            >
              {books.map((book) => (
                <option key={book.id} value={book.title}>
                  {book.title}
                </option>
              ))}
            </Select>
          </Flex>
        </Flex>
      </Flex>

      {loading ? (
        <Text>Loading...</Text>
      ) : (
        <Box overflowX="auto" maxWidth="100%">
          <Table  sx={{
                "tbody tr:nth-of-type(even)": { backgroundColor: "#f2f4f8" },
              }}>
            <Thead>
              <Tr>
                <Th>Teacher Name</Th>
                <Th>Book Title</Th>
                <Th>Issue Date</Th>
                <Th>Due Date</Th>

                <Th>Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {currentData.map((row) => (
                <Tr key={row.id}>
                  <Td>{row.member_name || "N/A"}</Td>
                  <Td>{row.title}</Td>
                  <Td>{row.issue_date}</Td>
                  <Td>{row.due_date}</Td>

                  <Td>{row.status}</Td>
                  <Td>
                    <Stack direction="row" spacing={2}>
                      <EditBookModal_Trs issueId={row.id} />
                      <IconButton
                        icon={<DeleteIcon />}
                        colorScheme="red"
                        size="sm"
                        onClick={() => deleteIssue(row.id)}
                      />
                    </Stack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <Flex justify="end" align="center" className="sb2" p={3} >
        <Button
          onClick={() => handlePageChange(currentPage - 1)}
          isDisabled={currentPage === 1}
        >
          Prev
        </Button>

        <Stack direction="row" spacing={2} mx={2}>
          {Array.from({ length: totalPages }, (_, i) => (
            <Button
              key={i}
              onClick={() => handlePageChange(i + 1)}
              colorScheme={currentPage === i + 1 ? "teal" : "gray"}
            >
              {i + 1}
            </Button>
          ))}
        </Stack>

        <Button
          onClick={() => handlePageChange(currentPage + 1)}
          isDisabled={currentPage === totalPages}
        >
          Next
        </Button>
      </Flex>
    </Box>
  );
};

export default AssignedTeachersBook;
